<script setup lang="ts">
import CourseCardRow, { type CourseCredit } from './CourseCardRow.vue';

export interface OnDemandCourse {
  id: string | number;
  title: string;
  duration: string;
  practiceAreas?: string[];
  /** Credential codes the row should render as chips (e.g. `['cle']`, `['cle', 'cpe']`). */
  credentialCodes: string[];
  /** LearningObjectType code — chip kind when `credentialCodes` is empty (Skills/Microlearning). */
  learningObjectTypeCode?: string;
  credits?: CourseCredit[];
  /** Original recording date — drives the "Recorded …" line on the card. */
  recordedDate?: string | null;
}

withDefaults(
  defineProps<{
    courses: OnDemandCourse[];
    courseLinkPrefix?: string;
  }>(),
  {
    courseLinkPrefix: '/course',
  }
);

const emit = defineEmits<{
  viewJurisdictions: [learningObjectId: string | number, credits: CourseCredit[]];
}>();

function handleViewJurisdictions(learningObjectId: string | number, credits: CourseCredit[]) {
  emit('viewJurisdictions', learningObjectId, credits);
}
</script>

<template>
  <div class="dashboard-section dashboard-section-full">
    <div class="section-header">
      <h2>New On-Demand Courses</h2>
    </div>
    <div v-if="courses.length > 0" class="course-cards-list">
      <CourseCardRow
        v-for="course in courses"
        :key="course.id"
        :id="course.id"
        :title="course.title"
        :duration="course.duration"
        :practice-areas="course.practiceAreas"
        :credential-codes="course.credentialCodes"
        :learning-object-type-code="course.learningObjectTypeCode"
        :credits="course.credits"
        :recorded-date="course.recordedDate"
        delivery-mode="OnDemand"
        action-label="View course"
        :action-url="`${courseLinkPrefix}/${course.id}`"
        compact
        @view-jurisdictions="handleViewJurisdictions"
      />
    </div>
    <div v-else class="empty-state-container inline">
      <div class="empty-state-icon">
        <bb-icon name="play_circle" decorative></bb-icon>
      </div>
      <h3 class="empty-state-title">No new on-demand courses</h3>
      <p class="empty-state-description">No new on-demand courses matching your jurisdictions. Check back soon!</p>
    </div>
  </div>
</template>
